import React, { useState, useEffect, useMemo } from 'react';
import { Search, Trophy } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getQuizLeaderboardUrl, getCustomQuizGetByIdUrl, getIeltsPerformanceUrl, getToeflPerformanceUrl } from '../utils/UrlUtilities';
import { useUser } from '../contexts/UserContext';
import CloseButton from './ui/CloseButton';

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

function rankBadgeColor(rank) {
    if (rank === 1) return '#f59e0b'; // gold
    if (rank === 2) return '#9ca3af'; // silver
    if (rank === 3) return '#b45309'; // bronze
    return '#7c3aed';
}

const QuizScoreLeaderboardModal = ({ quizId, submissionId, onClose }) => {
    const [scoreData, setScoreData] = useState(null);
    const [ieltsPerf, setIeltsPerf] = useState(null); // IELTS band scores for the current user's submission, if applicable
    const [toeflPerf, setToeflPerf] = useState(null); // TOEFL scaled/band scores for the current user's submission, if applicable
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const CallApi = useApiCaller();
    const { userState } = useUser();
    const myEmail = userState?.email;

    // Lock background scroll while the modal is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const fetchScoreAndLeaderboard = async () => {
            try {
                setLoading(true);
                const response = await CallApi({ url: getQuizLeaderboardUrl(quizId), method: 'GET', token: true });
                if (response.success) {
                    setScoreData(response.data);
                } else {
                    setError('Failed to fetch score and leaderboard.');
                }
            } catch (err) {
                console.error("Error fetching leaderboard:", err);
                setError('An error occurred while fetching score and leaderboard.');
            } finally {
                setLoading(false);
            }
        };
        fetchScoreAndLeaderboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizId]);

    // If this leaderboard was opened for a specific submission of a model test (IELTS or TOEFL),
    // pull the per-section + overall band scores so we can show them beside the raw score.
    useEffect(() => {
        if (!submissionId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const quizRes = await CallApi({ url: getCustomQuizGetByIdUrl(quizId), method: 'GET', token: true });
                if (cancelled || !quizRes.success) return;
                const kind = quizRes.data?.metadata?.test_kind;
                if (kind === 'ielts_model_test') {
                    const perfRes = await CallApi({ url: getIeltsPerformanceUrl(submissionId), method: 'GET', token: true });
                    if (!cancelled && perfRes.success) setIeltsPerf(perfRes.data);
                } else if (kind === 'toefl_model_test') {
                    const perfRes = await CallApi({ url: getToeflPerformanceUrl(submissionId), method: 'GET', token: true });
                    if (!cancelled && perfRes.success) setToeflPerf(perfRes.data);
                }
            } catch (err) { /* band scores are optional */ }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quizId, submissionId]);

    const board = scoreData?.leaderboard || [];
    const total = scoreData?.total_questions ?? 0;
    // Scores are out of the scored sections only (IELTS → Reading+Listening, ~80); ordinary
    // quizzes score out of every question, so fall back to `total`.
    const scoredMax = scoreData?.scored_max ?? total;
    const myRow = board.find(r => r.user_email === myEmail);
    const myScore = myRow?.score;
    const myRank = scoreData?.user_rank;

    const filtered = useMemo(() => {
        const list = scoreData?.leaderboard || [];
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(r =>
            (r.user_name || '').toLowerCase().includes(q) ||
            (r.institute || '').toLowerCase().includes(q)
        );
    }, [scoreData, search]);

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem',
        }}>
            <div style={{
                backgroundColor: '#ffffff', borderRadius: '0.9rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(760px, 96vw)',
                maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                {/* Sticky themed header with the close button pinned to the top-right */}
                <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '1.1rem 1.25rem', position: 'relative', flexShrink: 0 }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.35rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Trophy style={{ width: '1.3rem', height: '1.3rem' }} /> Score &amp; Leaderboard
                    </h2>
                </div>

                {/* Fixed summary + search (do not scroll) */}
                <div style={{ padding: '1rem 1.25rem 0.75rem', borderBottom: '1px solid #ede9fe', flexShrink: 0 }}>
                    {loading && <p className="mb-0">Loading…</p>}
                    {error && <p className="mb-0" style={{ color: '#dc2626' }}>{error}</p>}
                    {scoreData && (
                        <>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                                <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    Your Score: <strong>{myScore !== undefined ? `${myScore} / ${scoredMax}` : '—'}</strong>
                                </span>
                                <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    Your Rank: <strong>{myRank ?? '—'}</strong>
                                </span>
                                <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    Total Questions: <strong>{total}</strong>
                                </span>
                                <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    Total Score: <strong>{scoredMax}</strong>
                                </span>
                                <span className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}>
                                    Participants: <strong>{board.length}</strong>
                                </span>
                            </div>
                            {ieltsPerf && (
                                <div style={{ marginBottom: '0.9rem', padding: '0.75rem 0.9rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', background: '#faf5ff' }}>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 700, padding: '0.3rem 0.8rem', borderRadius: '9999px', color: '#fff', background: '#7c3aed' }}>
                                            Your overall band: {ieltsPerf.total_band != null ? ieltsPerf.total_band.toFixed(1) : '—'} / 9.0
                                        </span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {(ieltsPerf.sections || []).map(s => (
                                            <span key={s.name} className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.8rem', padding: '0.45rem 0.7rem' }}>
                                                {s.name}: {s.unscored ? 'Not scored' : <strong>band {s.band != null ? s.band.toFixed(1) : '—'} · {s.raw}/{s.max}</strong>}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.4rem' }}>
                                        Listening &amp; Reading are band-scored (0–9); overall is their average (nearest half-band). The ranking below uses the raw score.
                                    </div>
                                </div>
                            )}
                            {toeflPerf && (
                                <div style={{ marginBottom: '0.9rem', padding: '0.75rem 0.9rem', border: '1px solid #ddd6fe', borderRadius: '0.5rem', background: '#faf5ff' }}>
                                    <div className="d-flex flex-wrap gap-2" style={{ marginBottom: '0.5rem' }}>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 700, padding: '0.3rem 0.8rem', borderRadius: '9999px', color: '#fff', background: '#7c3aed' }}>
                                            Your overall: {toeflPerf.total_band != null ? toeflPerf.total_band.toFixed(1) : '—'} / 6.0
                                        </span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 700, padding: '0.3rem 0.8rem', borderRadius: '9999px', color: '#fff', background: '#6b7280' }}>
                                            {toeflPerf.total_scaled} / 120
                                        </span>
                                    </div>
                                    <div className="d-flex flex-wrap gap-2">
                                        {(toeflPerf.sections || []).map(s => (
                                            <span key={s.name} className="badge" style={{ background: '#ede9fe', color: '#5b21b6', fontSize: '0.8rem', padding: '0.45rem 0.7rem' }}>
                                                {s.name}: {s.unscored ? 'Not scored' : <strong>band {s.band != null ? s.band.toFixed(1) : '—'} · {s.scaled != null ? s.scaled : '—'}/30 · {s.raw}/{s.max}</strong>}
                                            </span>
                                        ))}
                                    </div>
                                    <div style={{ fontSize: '0.72rem', color: '#6b7280', marginTop: '0.4rem' }}>
                                        Reading &amp; Listening are scored on both scales — legacy 0–120 (each section 0–30) and the 1–6 band (overall = average of the scored sections, nearest half-band). Speaking &amp; Writing (email/discussion) are recorded but not scored. The ranking below uses the raw score.
                                    </div>
                                </div>
                            )}
                            <div className="input-group input-group-sm">
                                <span className="input-group-text" style={{ background: '#f5f3ff', borderColor: '#ddd6fe' }}>
                                    <Search style={{ width: '1rem', height: '1rem', color: '#7c3aed' }} />
                                </span>
                                <input
                                    className="form-control"
                                    style={{ borderColor: '#ddd6fe' }}
                                    placeholder="Search by name or institute…"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </div>

                {/* Scrollable leaderboard */}
                <div style={{ overflowY: 'auto', padding: '0.5rem 1.25rem 1.25rem', flex: 1 }}>
                    {scoreData && filtered.length === 0 && (
                        <p className="text-muted mt-3 mb-0">{board.length === 0 ? 'No submissions yet.' : 'No participants match your search.'}</p>
                    )}
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {filtered.map((entry) => {
                            const isMe = entry.user_email === myEmail;
                            const attempts = entry.attempts || [];
                            const attemptsText = attempts.map(a => `#${a.attempt}: ${a.score}/${scoredMax}`).join(', ');
                            return (
                                <li key={`${entry.rank}-${entry.user_email}`} style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem',
                                    padding: '0.6rem 0.5rem', borderBottom: '1px solid #f1f5f9',
                                    backgroundColor: isMe ? '#f5f3ff' : 'transparent', borderRadius: '0.4rem',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0 }}>
                                        <span style={{
                                            flexShrink: 0, minWidth: '1.75rem', height: '1.75rem', borderRadius: '9999px',
                                            background: rankBadgeColor(entry.rank), color: '#fff', fontWeight: 700, fontSize: '0.8rem',
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.4rem',
                                        }}>{entry.rank}</span>
                                        <div style={{ minWidth: 0 }}>
                                            <div style={{ fontWeight: isMe ? 700 : 600, color: '#1f2937' }}>
                                                {entry.user_name}{isMe && ' (You)'}
                                                {entry.institute && (
                                                    <span className="text-muted" style={{ fontWeight: 400 }}> · {entry.institute}</span>
                                                )}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                                                ({entry.attempts_count} {entry.attempts_count === 1 ? 'submission' : 'submissions'}
                                                {attemptsText ? ` — ${attemptsText}` : ''})
                                            </div>
                                        </div>
                                    </div>
                                    <span style={{ flexShrink: 0, fontWeight: 700, color: '#5b21b6' }}>
                                        {entry.score} / {scoredMax}
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default QuizScoreLeaderboardModal;
